// Lógica pura del pago dividido: una venta del local se paga con varios medios. Cada parte cubre un monto en la moneda
// del ticket y se cobra en la moneda del ticket o en pesos; las partes deben cubrir exactamente lo que falta cobrar.

export const SPLIT_METHODS = { CASH: '1', CARD: '2', DEBIT: '3', TRANSFER: '6' };
export const MAX_SPLIT_PARTS = 8;
const TOLERANCE = 0.005;

export const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

// Acepta "1.234,50", "1234.5" o "1234,5".
export const parseMoney = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  const sanitized = String(raw ?? '').replace(/\s/g, '');
  if (!sanitized) return 0;
  const hasComma = sanitized.includes(',');
  const hasDot = sanitized.includes('.');
  let normalized = sanitized;
  if (hasComma && hasDot) {
    normalized = sanitized.lastIndexOf(',') > sanitized.lastIndexOf('.')
      ? sanitized.replace(/\./g, '').replace(',', '.')
      : sanitized.replace(/,/g, '');
  } else if (hasComma) {
    normalized = sanitized.replace(',', '.');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

let partSequence = 0;
export const newPart = (method, amount, currency) => ({
  uid: `part-${Date.now()}-${partSequence += 1}`,
  method,
  amount: amount > 0 ? String(round2(amount)) : '',
  currency,
  // Cobro del POS de esta parte: la clave de idempotencia sobrevive a los reintentos, el id se guarda al abrirse el cobro
  // y la transacción aprobada queda fija (la parte ya no se puede editar ni quitar).
  idempotencyKey: null,
  pendingTransactionId: null,
  transactionId: null,
});

export const partAmount = (part) => round2(parseMoney(part.amount));

export const coveredAmount = (parts) => round2(parts.reduce((sum, part) => sum + partAmount(part), 0));

// Positivo: falta cobrar; negativo: las partes se pasan.
export const remainingAmount = (due, parts) => round2(due - coveredAmount(parts));

// Lo que se cobra de una parte en su moneda de cobro (pesos al tipo de cambio del ticket, igual que el backend).
export const collectedAmount = (part, ticketCurrency, ticketRate) => {
  const amount = partAmount(part);
  if (part.currency === ticketCurrency || !ticketRate) return amount;
  return round2(amount * ticketRate);
};

// Una parte que va por el POS integrado (tarjeta con POS disponible).
export const isPosPart = (part, posAvailable) =>
  Boolean(posAvailable) && [SPLIT_METHODS.CARD, SPLIT_METHODS.DEBIT].includes(String(part.method));

// Devuelve un mensaje de error o null si las partes son válidas para cobrar `due`.
export const splitError = (parts, due) => {
  if (parts.length < 2) return 'Agregá al menos dos medios de pago.';
  if (parts.length > MAX_SPLIT_PARTS) return `Máximo ${MAX_SPLIT_PARTS} medios de pago.`;
  if (parts.some((part) => !part.method)) return 'Elegí el medio de cada pago.';
  if (parts.some((part) => partAmount(part) <= 0)) return 'Cada pago debe tener un monto mayor a cero.';
  const remaining = remainingAmount(due, parts);
  if (remaining > TOLERANCE) return `Falta cobrar ${remaining.toFixed(2)}.`;
  if (remaining < -TOLERANCE) return `Los pagos se pasan por ${Math.abs(remaining).toFixed(2)}.`;
  return null;
};

export const buildPaymentsPayload = (parts) =>
  parts.map((part) => ({
    method: parseInt(part.method, 10),
    amount: partAmount(part),
    currency: part.currency,
    ...(part.transactionId ? { payment_transaction_id: part.transactionId } : {}),
  }));

// Reparte `due` en dos partes iguales (la segunda toma el resto) para arrancar el modo dividido.
export const initialParts = (due, ticketCurrency, posAvailable) => {
  const first = round2(due / 2);
  return [
    newPart(SPLIT_METHODS.CASH, first, ticketCurrency),
    newPart(posAvailable ? SPLIT_METHODS.CARD : SPLIT_METHODS.TRANSFER, round2(due - first), ticketCurrency),
  ];
};

const METHOD_LABELS = { [SPLIT_METHODS.CASH]: 'Efectivo', [SPLIT_METHODS.CARD]: 'Tarjeta', [SPLIT_METHODS.DEBIT]: 'Débito', [SPLIT_METHODS.TRANSFER]: 'Transferencia' };

// Qué se está cobrando mientras se espera al POS: una fila por medio de pago, con su monto en la moneda en que se cobra y
// su estado (approved = ya cobrado en el POS, current = el POS lo tiene ahora, waiting = falta cobrarlo en el POS,
// registered = no pasa por el POS: se registra junto con la venta).
export const awaitingRows = (parts, { posAvailable, currentUid, ticketCurrency, ticketRate }) =>
  parts.map((part) => {
    const pos = isPosPart(part, posAvailable);
    let state = 'registered';
    if (pos) {
      if (part.transactionId) state = 'approved';
      else if (part.uid === currentUid) state = 'current';
      else state = 'waiting';
    }
    return {
      uid: part.uid,
      pos,
      state,
      label: pos ? 'Tarjeta (POS)' : (METHOD_LABELS[String(part.method)] || 'Otro medio'),
      amount: collectedAmount(part, ticketCurrency, ticketRate),
      currency: part.currency,
    };
  });

// "Cobro 1 de 2": cuál es el cobro del POS que está en curso, contando solo los medios que pasan por el POS.
export const posProgress = (rows) => {
  const posRows = rows.filter((row) => row.pos);
  const approved = posRows.filter((row) => row.state === 'approved').length;
  return { count: posRows.length, step: Math.min(approved + 1, posRows.length) };
};
